import React, { ReactNode, useEffect, useState } from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
// NOTE: This component uses an Icon size not supported by Icon component
import { User } from '@vio/atlas-assets';
import { anyPass, isEmpty, isNil } from 'ramda';
import { SizesType } from 'types/Sizes';
import { ColorPath } from 'types/Theme';
import { getFirstAndLastInitials } from '@daedalus/core/src/auth/business/authentication';
import { getCssColorByPath } from '../../../utils/colors';
import { Text, TextVariant } from '../Text';
type AvatarSize = Extract<SizesType, 'md' | 'lg' | 'xl'>;
interface Props {
  /** Border color */
  borderColorPath: ColorPath;
  /** Bottom icon */
  bottomIcon?: ReactNode;
  /** Pass through classname to allow styles overrides */
  className?: string;
  /** The size of the Avatar */
  size?: AvatarSize;
  /** Appearance of avatar */
  type?: AvatarType;
  /** User's picture */
  picture?: string;
  /** User's name */
  name?: string;
}
interface UserPhotoFallbackProps {
  size: AvatarSize;
  name?: Props['name'];
}
interface StandardAvatarProps {
  size: AvatarSize;
  name?: Props['name'];
}
interface AvatarStyles {
  borderColorValue?: string;
  name?: Props['name'];
  size: AvatarSize;
}
export enum AvatarType {
  Picture = 'picture',
  Icon = 'icon',
}
export const avatarSizesMap = {
  md: {
    size: 32,
    iconSize: 16,
    padding: 4,
    border: 1,
    textVariant: 'titleXS'
  },
  lg: {
    size: 72,
    iconSize: 40,
    padding: 9,
    border: 2,
    textVariant: 'titleM'
  },
  xl: {
    size: 96,
    iconSize: 48,
    padding: 12,
    border: 3,
    textVariant: 'titleXL'
  }
};
const _exp = /*#__PURE__*/() => ({
  size
}) => ({
  md: {
    size: 32,
    iconSize: 16,
    padding: 4,
    border: 1,
    textVariant: "titleXS"
  },
  lg: {
    size: 72,
    iconSize: 40,
    padding: 9,
    border: 2,
    textVariant: "titleM"
  },
  xl: {
    size: 96,
    iconSize: 48,
    padding: 12,
    border: 3,
    textVariant: "titleXL"
  }
})[size].padding;
const _exp2 = /*#__PURE__*/() => ({
  size
}) => ({
  md: {
    size: 32,
    iconSize: 16,
    padding: 4,
    border: 1,
    textVariant: "titleXS"
  },
  lg: {
    size: 72,
    iconSize: 40,
    padding: 9,
    border: 2,
    textVariant: "titleM"
  },
  xl: {
    size: 96,
    iconSize: 48,
    padding: 12,
    border: 3,
    textVariant: "titleXL"
  }
})[size].border;
const _exp3 = /*#__PURE__*/() => ({
  borderColorValue
}) => borderColorValue!;
const AvatarWrapper = /*#__PURE__*/styled('div')({
  name: "AvatarWrapper",
  class: "a2ewuvz",
  propsAsIs: false,
  vars: {
    "a2ewuvz-0": [_exp(), "px"],
    "a2ewuvz-1": [_exp2(), "px"],
    "a2ewuvz-2": [_exp3()]
  }
});
const _exp5 = /*#__PURE__*/() => ({
  size
}) => ({
  md: {
    size: 32,
    iconSize: 16,
    padding: 4,
    border: 1,
    textVariant: "titleXS"
  },
  lg: {
    size: 72,
    iconSize: 40,
    padding: 9,
    border: 2,
    textVariant: "titleM"
  },
  xl: {
    size: 96,
    iconSize: 48,
    padding: 12,
    border: 3,
    textVariant: "titleXL"
  }
})[size].size;
export const PictureAvatar = /*#__PURE__*/styled('img')({
  name: "PictureAvatar",
  class: "p1sib5mm",
  propsAsIs: false,
  vars: {
    "p1sib5mm-0": [_exp5(), "px"]
  }
});
const _exp7 = /*#__PURE__*/() => ({
  size
}) => ({
  md: {
    size: 32,
    iconSize: 16,
    padding: 4,
    border: 1,
    textVariant: "titleXS"
  },
  lg: {
    size: 72,
    iconSize: 40,
    padding: 9,
    border: 2,
    textVariant: "titleM"
  },
  xl: {
    size: 96,
    iconSize: 48,
    padding: 12,
    border: 3,
    textVariant: "titleXL"
  }
})[size].size;
export const DefaultPictureWrapper = /*#__PURE__*/styled('div')({
  name: "DefaultPictureWrapper",
  class: "dfxg84l",
  propsAsIs: false,
  vars: {
    "dfxg84l-0": [_exp7(), "px"]
  }
});
export const IconWrapper = /*#__PURE__*/styled('div')({
  name: "IconWrapper",
  class: "i1kpazgp",
  propsAsIs: false
});
const StandardAvatar = ({
  size
}: StandardAvatarProps) => <DefaultPictureWrapper size={size}>
    <User size={avatarSizesMap[size].iconSize} />
  </DefaultPictureWrapper>;
const UserPhotoFallback = ({
  name,
  size
}: UserPhotoFallbackProps) => {
  const initials = getFirstAndLastInitials(name || '');
  return <DefaultPictureWrapper data-id="ProfilePicture" size={size}>
      <Text variant={avatarSizesMap[size].textVariant as TextVariant}>
        {initials}
      </Text>
    </DefaultPictureWrapper>;
};
export const getShowAvatar = ({
  type,
  name,
  picture
}: {
  type: AvatarType;
  name?: string;
  picture?: string;
}) => type === AvatarType.Icon || anyPass([isNil, isEmpty])(name) && anyPass([isNil, isEmpty])(picture);
export const Avatar = ({
  borderColorPath,
  picture,
  name,
  bottomIcon,
  size = 'md',
  type = AvatarType.Icon,
  className
}: Props) => {
  const [shouldShowFallbackAvatar, setShouldShowFallbackAvatar] = useState(true);
  const shouldShowIconAvatar = getShowAvatar({
    type,
    name,
    picture
  });
  const borderColorValue = getCssColorByPath(borderColorPath);
  useEffect(() => {
    if (!picture) return setShouldShowFallbackAvatar(true);
    setShouldShowFallbackAvatar(false);
    const image = new Image();
    image.addEventListener('error', () => setShouldShowFallbackAvatar(true));
    image.src = picture;
  }, [picture]);
  const AvatarElement = shouldShowIconAvatar ? StandardAvatar : shouldShowFallbackAvatar ? UserPhotoFallback : PictureAvatar;
  return <AvatarWrapper borderColorValue={borderColorValue} size={size} className={cx(className, Boolean(borderColorValue) && '--border')}>
      <AvatarElement data-id="ProfilePicture" className="fs-exclude" src={picture} size={size} alt={name} title={name} name={name} />
      {Boolean(bottomIcon) && <IconWrapper>{bottomIcon}</IconWrapper>}
    </AvatarWrapper>;
};

require("./Avatar.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Avatar.tsx");